import type { VectorStoreUploadedFile } from '../types';
import {
  MAX_VECTOR_STORE_BESKRIVELSE,
  MAX_VECTOR_STORE_NAVN,
  VECTOR_STORE_ALLOWED_EXTENSIONS,
} from './vectorStoreFormLimits';

export interface VectorStoreFormDraft {
  navn: string;
  beskrivelse: string;
  tilladChunking: boolean;
  synligForLaerere: boolean;
  uploadedFiles: VectorStoreUploadedFile[];
}

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) {
    return '';
  }
  return filename.slice(dot).toLowerCase();
}

export function vectorStoreFileKey(
  file: File | VectorStoreUploadedFile,
): string {
  const lastModified = 'lastModified' in file ? file.lastModified : 0;
  return `${file.name}::${file.size}::${lastModified}`;
}

export function validateVectorStoreFiles(
  files: VectorStoreUploadedFile[],
): string | null {
  if (files.length === 0) {
    return 'Vælg mindst én fil at uploade.';
  }
  for (const file of files) {
    const ext = extensionOf(file.name);
    if (!VECTOR_STORE_ALLOWED_EXTENSIONS.includes(ext as (typeof VECTOR_STORE_ALLOWED_EXTENSIONS)[number])) {
      return `«${file.name}» skal være .md, .txt, .pdf, .docx eller .json.`;
    }
  }
  return null;
}

export function validateVectorStoreMetadata(draft: Pick<VectorStoreFormDraft, 'navn' | 'beskrivelse'>): string | null {
  const navn = draft.navn.trim();
  if (!navn) {
    return 'Navn er påkrævet.';
  }
  if (navn.length > MAX_VECTOR_STORE_NAVN) {
    return `Navn må højst være ${MAX_VECTOR_STORE_NAVN} tegn.`;
  }
  if (draft.beskrivelse.length > MAX_VECTOR_STORE_BESKRIVELSE) {
    return `Beskrivelse må højst være ${MAX_VECTOR_STORE_BESKRIVELSE} tegn.`;
  }
  return null;
}

export function validateVectorStoreForm(draft: VectorStoreFormDraft): string | null {
  const metadataError = validateVectorStoreMetadata(draft);
  if (metadataError) {
    return metadataError;
  }
  return validateVectorStoreFiles(draft.uploadedFiles);
}

export function fileToUploadedMetadata(file: File): VectorStoreUploadedFile {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
  };
}

export function mergeUniqueFiles(existing: File[], incoming: File[]): File[] {
  const seen = new Set(existing.map(vectorStoreFileKey));
  const merged = [...existing];
  for (const file of incoming) {
    const key = vectorStoreFileKey(file);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(file);
  }
  return merged;
}

export function removeFileByKey(files: File[], key: string): File[] {
  return files.filter((file) => vectorStoreFileKey(file) !== key);
}
